import React, { useState, useMemo } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ChevronRight, FolderTree } from "lucide-react";
import { storeApi } from "@/lib/storefront";
import useStoreRefresh from "@/hooks/useStoreRefresh";

/**
 * Persistent left rail with collapsible category tree.
 * - Renders only parent categories by default (compact, ~38 px rows)
 * - Tapping a parent reveals its subcategories inline
 * - Auto-expands the parent that contains the currently-active category
 * - Truncates long names with ellipsis
 */
export default function CategorySidebar({ activeSlug }) {
  const params = useParams();
  const location = useLocation();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState(new Set());

  useStoreRefresh(() => {
    setLoading(true);
    storeApi.get("/categories/tree")
      .then((r) => setTree(r.data || []))
      .catch(() => setTree([]))
      .finally(() => setLoading(false));
  }, []);

  const current = activeSlug || params.slug || null;
  const isAll = !current && (location.pathname === "/catalog" || location.pathname.startsWith("/catalog?"));

  // Auto-expand the parent that owns the active subcategory
  const autoOpenParentId = useMemo(() => {
    if (!current) return null;
    for (const p of tree) {
      if (p.slug === current) return null;
      if (p.children?.some((c) => c.slug === current)) return p.id;
    }
    return null;
  }, [current, tree]);

  const isOpen = (id) => openIds.has(id) || id === autoOpenParentId;

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <aside
      className="hidden md:block md:w-[230px] md:flex-shrink-0"
      data-testid="category-sidebar"
      aria-label="Browse categories"
    >
      <div className="md:sticky md:top-28">
        <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">
          <FolderTree size={12} strokeWidth={1.6} />
          <span>Categories</span>
        </div>

        {loading ? (
          <div className="space-y-1.5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-9 w-3/4 animate-pulse rounded bg-[#E8E2D2]" />
            ))}
          </div>
        ) : (
          <nav className="space-y-0.5" data-testid="category-sidebar-list">
            <AllRow active={isAll} />
            {tree.map((parent) => (
              <ParentNode
                key={parent.id}
                node={parent}
                open={isOpen(parent.id)}
                onToggle={() => toggle(parent.id)}
                activeSlug={current}
              />
            ))}
          </nav>
        )}

        <div className="mt-8 rounded-md border border-[#DDD6C5] bg-white p-4 text-xs">
          <div className="font-display text-sm text-[#1A1A1A]">Not sure where to start?</div>
          <p className="mt-1 leading-relaxed text-[#2D2823]">Browse our gift guide or chat with us — we'll point you to the right pieces.</p>
          <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-[#7D8471] hover:text-[#1A1A1A]">
            Get in touch <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </aside>
  );
}

function AllRow({ active }) {
  return (
    <Link
      to="/catalog"
      className={`flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors ${
        active ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-[#E8E2D2]"
      }`}
      data-testid="cat-side-all"
    >
      <span className="truncate">All products</span>
    </Link>
  );
}

function ParentNode({ node, open, onToggle, activeSlug }) {
  const hasChildren = node.children?.length > 0;
  const isActive = activeSlug === node.slug;

  return (
    <div data-testid={`cat-side-${node.slug}`}>
      <div
        className={`group flex h-9 items-center gap-1.5 rounded-md transition-colors ${
          isActive ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-[#E8E2D2]"
        }`}
      >
        <Link
          to={`/c/${node.slug}`}
          className="flex min-w-0 flex-1 items-center gap-1 px-3 text-[13.5px]"
          data-testid={`cat-side-parent-${node.slug}`}
        >
          <span className="truncate">{node.name}</span>
        </Link>
        {hasChildren && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={open ? "Collapse" : "Expand"}
            aria-expanded={open}
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md mr-1 hover:bg-black/10 ${isActive ? "text-white/80" : "text-[#7D8471]"}`}
            data-testid={`cat-side-toggle-${node.slug}`}
          >
            <ChevronRight size={14} strokeWidth={1.8} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <ul className="my-0.5 ml-3 border-l border-[#DDD6C5] pl-2.5">
          {node.children.map((child) => (
            <li key={child.id}>
              <Link
                to={`/c/${child.slug}`}
                className={`flex h-8 items-center rounded-md px-2.5 text-[12.5px] transition-colors ${
                  activeSlug === child.slug
                    ? "bg-[#7D8471] text-white"
                    : "text-[#2D2823] hover:bg-[#E8E2D2] hover:text-[#1A1A1A]"
                }`}
                title={child.name}
                data-testid={`cat-side-child-${child.slug}`}
              >
                <span className="truncate">{child.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
